// ScoringSystem - Simplified 4-method API for scoring

import { getHitQualityByDistance, getStreakMultiplier, SCORING_ZONES } from '@/utils/timing';
import {
  SCORING_V2,
  type ScoringQuality,
  type Grade,
  diffMult,
  precisionFromOffsetPx,
  gradeFromAccuracyPct,
  roundTo,
} from '@/config/scoring';

// Quality types
export type ScoredQuality = ScoringQuality;
export type TimingFeedback = 'EARLY' | 'LATE' | 'ON_TIME';

// Result types returned to GameScene
export interface KeydownResult {
  quality: ScoredQuality;
  timing: TimingFeedback;
}

export interface HitResult {
  quality: ScoredQuality;
  points: number;
  showBreakFeedback: boolean;
  milestoneReached: number | null; // null or the new multiplier (2, 3, 4)
  previousStreak: number;
}

export interface PenaltyResult {
  points: number;
  showBreakFeedback: boolean;
  previousStreak: number;
}

// Game state
export interface GameState {
  score: number;
  streak: number;
  multiplier: number;
  maxStreak: number;
  perfectHits: number;
  goodHits: number;
  okHits: number;
  badHits: number;
  passedUnhit: number;
  whiffs: number;
}

// Quality values for averaging
const QUALITY_VALUES: Record<ScoredQuality, number> = {
  perfect: 3,
  good: 2,
  ok: 1,
  bad: 0,
};

const STREAK_FEEDBACK_THRESHOLD = 10;

export class ScoringSystem {
  private state: GameState;
  private totalNotes: number;
  private accuracyPointsSum: number = 0;

  // Pending keydown quality for two-point scoring
  private pendingKeydown:
    | { qualityDown: ScoredQuality; precisionDown: number }
    | null = null;

  constructor(totalNotes: number) {
    this.totalNotes = totalNotes;

    this.state = {
      score: 0,
      streak: 0,
      multiplier: 1,
      maxStreak: 0,
      perfectHits: 0,
      goodHits: 0,
      okHits: 0,
      badHits: 0,
      passedUnhit: 0,
      whiffs: 0,
    };
  }

  addTotalNotes(delta: number): void {
    if (!Number.isFinite(delta) || delta <= 0) return;
    this.totalNotes += delta;
  }

  getState(): Readonly<GameState> {
    return { ...this.state };
  }

  // ========== METHOD 1: Record Keydown Timing ==========
  recordKeydownTiming(centerOffset: number): KeydownResult {
    const quality = getHitQualityByDistance(centerOffset);
    const precisionDown = precisionFromOffsetPx(centerOffset, SCORING_ZONES.ok);
    this.pendingKeydown = { qualityDown: quality, precisionDown };

    const timing = this.getTimingFeedback(centerOffset);

    return { quality, timing };
  }

  // ========== METHOD 2: Score Hit (on keyup) ==========
  scoreHit(centerOffset: number, symbolMatches: boolean, speedPxPerSec: number): HitResult {
    const keydown = this.pendingKeydown;
    this.pendingKeydown = null;

    // Symbol mismatch = automatic bad
    if (!symbolMatches) {
      return this.applyHit({
        quality: 'bad',
        precision: 0,
        speedPxPerSec,
      });
    }

    if (!keydown) {
      throw new Error('scoreHit called without prior recordKeydownTiming');
    }

    // Get keyup quality and combine with keydown
    const keyupQuality = getHitQualityByDistance(centerOffset);
    const precisionUp = precisionFromOffsetPx(centerOffset, SCORING_ZONES.ok);

    const keydownValue = QUALITY_VALUES[keydown.qualityDown];
    const keyupValue = QUALITY_VALUES[keyupQuality];
    const averageValue = (keydownValue + keyupValue) / 2;
    const combinedQuality = this.valueToQuality(averageValue);

    const precision = (keydown.precisionDown + precisionUp) / 2;
    return this.applyHit({
      quality: combinedQuality,
      precision,
      speedPxPerSec,
    });
  }

  // ========== METHOD 3: Record Whiff ==========
  recordWhiff(speedPxPerSec: number): PenaltyResult {
    this.pendingKeydown = null;
    this.state.whiffs++;
    return this.applyWhiffPenalty(speedPxPerSec);
  }

  // ========== METHOD 4: Record Note Passed Unhit ==========
  recordPassedUnhit(): PenaltyResult {
    this.state.passedUnhit++;
    this.accuracyPointsSum += 0;
    return this.applyPassedUnhit();
  }

  // ========== RESULTS ==========

  getResults(): {
    score: number;
    maxStreak: number;
    accuracy: number;
    grade: Grade;
  } {
    const accuracyPct = this.totalNotes > 0
      ? (this.accuracyPointsSum / this.totalNotes) * 100
      : 0;
    const accuracy = roundTo(accuracyPct, 2);
    const grade = gradeFromAccuracyPct(accuracy);

    return {
      score: this.state.score,
      maxStreak: this.state.maxStreak,
      accuracy,
      grade,
    };
  }

  // ========== PRIVATE HELPERS ==========

  private applyHit(input: { quality: ScoredQuality; precision: number; speedPxPerSec: number }): HitResult {
    const { quality } = input;
    // Update hit counts
    switch (quality) {
      case 'perfect': this.state.perfectHits++; break;
      case 'good': this.state.goodHits++; break;
      case 'ok': this.state.okHits++; break;
      case 'bad': this.state.badHits++; break;
    }

    const precision = quality === 'bad' ? 0 : Math.max(0, Math.min(1, input.precision));
    const accPoints = SCORING_V2.accWeight[quality] * (0.5 + 0.5 * precision);
    this.accuracyPointsSum += accPoints;

    // Handle streak
    let showBreakFeedback = false;
    let milestoneReached: number | null = null;
    let previousStreak = this.state.streak;

    if (quality !== 'bad') {
      // Good hit - increment streak
      const previousMultiplier = this.state.multiplier;
      this.state.streak++;
      this.state.maxStreak = Math.max(this.state.maxStreak, this.state.streak);
      this.state.multiplier = getStreakMultiplier(this.state.streak);

      const base = SCORING_V2.BASE * SCORING_V2.qualityWeight[quality];
      const bonus = SCORING_V2.precisionBonusMax[quality] * precision;
      const points = Math.round((base + bonus) * this.state.multiplier * diffMult(input.speedPxPerSec));
      this.state.score += points;
      this.state.score = Math.max(0, this.state.score);

      if (this.state.multiplier > previousMultiplier) {
        milestoneReached = this.state.multiplier;
      }

      return { quality, points, showBreakFeedback, milestoneReached, previousStreak };
    }

    // Bad hit - break streak, 0 points
    const breakResult = this.breakStreak();
    showBreakFeedback = breakResult.showBreakFeedback;
    previousStreak = breakResult.previousStreak;

    const points = 0;
    this.state.score = Math.max(0, this.state.score);

    return { quality, points, showBreakFeedback, milestoneReached, previousStreak };
  }

  private applyPassedUnhit(): PenaltyResult {
    const { previousStreak, showBreakFeedback } = this.breakStreak();
    const points = 0;
    this.state.score = Math.max(0, this.state.score);
    return { points, showBreakFeedback, previousStreak };
  }

  private applyWhiffPenalty(speedPxPerSec: number): PenaltyResult {
    const { previousStreak, showBreakFeedback } = this.breakStreak();
    const points = -Math.round(SCORING_V2.WHIFF_BASE * diffMult(speedPxPerSec));
    this.state.score += points;
    this.state.score = Math.max(0, this.state.score);
    return { points, showBreakFeedback, previousStreak };
  }

  private valueToQuality(value: number): ScoredQuality {
    if (value >= 3) return 'perfect';
    if (value >= 2) return 'good';
    if (value >= 1) return 'ok';
    return 'bad';
  }

  private shouldShowBreakFeedback(previousStreak: number): boolean {
    return previousStreak >= STREAK_FEEDBACK_THRESHOLD;
  }

  private breakStreak(): { previousStreak: number; showBreakFeedback: boolean } {
    const previousStreak = this.state.streak;
    const showBreakFeedback = this.shouldShowBreakFeedback(previousStreak);
    this.state.streak = 0;
    this.state.multiplier = 1;
    return { previousStreak, showBreakFeedback };
  }

  private getTimingFeedback(pixelOffset: number): TimingFeedback {
    if (Math.abs(pixelOffset) <= SCORING_ZONES.perfect) return 'ON_TIME';
    return pixelOffset > 0 ? 'EARLY' : 'LATE';
  }
}
